// DesktopDevChatModelPicker - Search bar + sort/filter dropdowns + results panel
// Mimics BrowseSearchBar: learn-style pill search, sort/filter dropdowns
// Results appear in a dropdown-like panel when search is executed (Enter/button) or when focused

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { DropdownSelect } from '../../../../../components';
import './DesktopDevChatModelPicker.css';

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'context', label: 'Context length' },
  { value: 'architecture', label: 'Architecture' },
  { value: 'provider', label: 'Provider' },
];

const SORT_DIRECTION_OPTIONS = [
  { value: 'asc', label: 'A–Z' },
  { value: 'desc', label: 'Z–A' },
];

function ModelItemContent({ model }) {
  const name = model.displayName || model.name || 'Unknown';
  const overview = model.metadata?.insightsOverview;
  return (
    <span className="desktop-dev-chat-model-picker__item-content">
      <span className="desktop-dev-chat-model-picker__item-name">{name}</span>
      {overview?.architecture && (
        <span className="desktop-dev-chat-model-picker__badge">{overview.architecture}</span>
      )}
      {overview?.contextLength != null && (
        <span className="desktop-dev-chat-model-picker__badge">{overview.contextLength} CTX</span>
      )}
    </span>
  );
}

function getArchitectureOptions(models) {
  const set = new Set();
  models.forEach((m) => {
    const arch = m.metadata?.insightsOverview?.architecture;
    if (arch) set.add(arch);
  });
  const opts = [{ value: 'all', label: 'All architectures' }];
  [...set].sort().forEach((a) => opts.push({ value: a, label: a }));
  return opts;
}

export function DesktopDevChatModelPicker({
  models = [],
  selectedModelId,
  onSelectModel,
  disabled = false,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterArchitecture, setFilterArchitecture] = useState('all');
  const [resultsOpen, setResultsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const architectureOptions = getArchitectureOptions(models);

  const getFilteredModels = useCallback(() => {
    let filtered = models.filter((m) => {
      if (filterArchitecture !== 'all') {
        const arch = m.metadata?.insightsOverview?.architecture;
        if (arch !== filterArchitecture) return false;
      }
      if (!searchQuery.trim()) return true;
      const search = searchQuery.toLowerCase();
      const name = (m.displayName || m.name || m.id || '').toLowerCase();
      const provider = (m.metadata?.insightsOverview?.publisher || '').toLowerCase();
      const arch = (m.metadata?.insightsOverview?.architecture || '').toLowerCase();
      return name.includes(search) || provider.includes(search) || arch.includes(search);
    });

    filtered = [...filtered].sort((a, b) => {
      const insightsA = a.metadata?.insightsOverview || {};
      const insightsB = b.metadata?.insightsOverview || {};
      let aVal, bVal;

      switch (sortBy) {
        case 'name':
          aVal = (a.displayName || a.name || a.id || '').toLowerCase();
          bVal = (b.displayName || b.name || b.id || '').toLowerCase();
          break;
        case 'provider':
          aVal = (insightsA.publisher || '').toLowerCase();
          bVal = (insightsB.publisher || '').toLowerCase();
          break;
        case 'architecture':
          aVal = (insightsA.architecture || '').toLowerCase();
          bVal = (insightsB.architecture || '').toLowerCase();
          break;
        case 'context':
          aVal = insightsA.contextLength != null ? Number(insightsA.contextLength) : -1;
          bVal = insightsB.contextLength != null ? Number(insightsB.contextLength) : -1;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [models, searchQuery, sortBy, sortDirection, filterArchitecture]);

  const filteredModels = getFilteredModels();
  const selectedModel = models.find((m) => m.id === selectedModelId);

  const runSearch = useCallback(() => {
    setResultsOpen(true);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runSearch();
    }
  };

  const handleSelectModel = (model) => {
    onSelectModel?.(model.id);
    setResultsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setResultsOpen(false);
      }
    };
    if (resultsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [resultsOpen]);

  return (
    <div className="desktop-dev-chat-model-picker" ref={containerRef}>
      <div className="desktop-dev-chat-model-picker__row">
        <div className="desktop-dev-chat-model-picker__search">
          <div className="desktop-dev-chat-model-picker__search-input-wrap">
            <i className="fa-solid fa-magnifying-glass desktop-dev-chat-model-picker__search-icon" aria-hidden />
            <input
              ref={inputRef}
              type="text"
              placeholder={selectedModel ? '' : 'Search models...'}
              value={
                resultsOpen
                  ? searchQuery
                  : selectedModel
                    ? (selectedModel.displayName || selectedModel.name || 'Unknown')
                    : searchQuery
              }
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (models.length > 0) {
                  setResultsOpen(true);
                  if (selectedModel) setSearchQuery('');
                }
              }}
              className="desktop-dev-chat-model-picker__input"
              disabled={disabled}
            />
          </div>
          {selectedModel && !resultsOpen && !disabled && (() => {
            const overview = selectedModel.metadata?.insightsOverview;
            return (overview?.architecture || overview?.contextLength != null) ? (
              <div className="desktop-dev-chat-model-picker__selected-badges">
                {overview?.architecture && (
                  <span className="desktop-dev-chat-model-picker__badge">{overview.architecture}</span>
                )}
                {overview?.contextLength != null && (
                  <span className="desktop-dev-chat-model-picker__badge">{overview.contextLength} CTX</span>
                )}
              </div>
            ) : null;
          })()}
        </div>

        <div className="desktop-dev-chat-model-picker__filter">
          <label htmlFor="dev-chat-sort-select" className="desktop-dev-chat-model-picker__label">
            Sort by
          </label>
          <DropdownSelect
            id="dev-chat-sort-select"
            value={sortBy}
            options={SORT_OPTIONS}
            onValueChange={setSortBy}
            disabled={disabled}
            size="sm"
            className="desktop-dev-chat-model-picker__select"
            menuClassName="app-management-sort-menu"
          />
        </div>

        <div className="desktop-dev-chat-model-picker__filter">
          <label htmlFor="dev-chat-direction-select" className="desktop-dev-chat-model-picker__label">
            Order
          </label>
          <DropdownSelect
            id="dev-chat-direction-select"
            value={sortDirection}
            options={SORT_DIRECTION_OPTIONS}
            onValueChange={setSortDirection}
            disabled={disabled}
            size="sm"
            className="desktop-dev-chat-model-picker__select desktop-dev-chat-model-picker__select--narrow"
            menuClassName="app-management-sort-menu"
          />
        </div>

        <div className="desktop-dev-chat-model-picker__filter">
          <label htmlFor="dev-chat-arch-select" className="desktop-dev-chat-model-picker__label">
            Architecture
          </label>
          <DropdownSelect
            id="dev-chat-arch-select"
            value={filterArchitecture}
            options={architectureOptions}
            onValueChange={setFilterArchitecture}
            disabled={disabled}
            size="sm"
            className="desktop-dev-chat-model-picker__select"
            menuClassName="app-management-sort-menu"
          />
        </div>
      </div>

      {resultsOpen && (
        <div className="desktop-dev-chat-model-picker__results">
          {filteredModels.length === 0 ? (
            <div className="desktop-dev-chat-model-picker__empty">No models match</div>
          ) : (
            <ul className="desktop-dev-chat-model-picker__list">
              {filteredModels.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className={`desktop-dev-chat-model-picker__item ${
                      m.id === selectedModelId ? 'desktop-dev-chat-model-picker__item--selected' : ''
                    }`}
                    onClick={() => handleSelectModel(m)}
                  >
                    <ModelItemContent model={m} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

    </div>
  );
}
